/**
 * Product Upsell Bundle Component
 * Single toggle with product style selection and variant picker
 */

if (!customElements.get('product-upsell-bundle')) {
  class ProductUpsellBundle extends HTMLElement {
    constructor() {
      super();
      this.mainProductFormId = this.dataset.mainProductForm;
      this.mainProductColor = this.dataset.mainProductColor;

      // Elements
      this.masterToggle = this.querySelector('[data-upsell-master-toggle]');
      this.content = this.querySelector('[data-upsell-bundle-content]');
      this.productRadios = this.querySelectorAll('[data-upsell-product-radio]');
      this.productCards = this.querySelectorAll('[data-upsell-card]');
      this.variantSelectors = this.querySelectorAll('[data-upsell-variant-selector]');
      this.priceDisplay = this.querySelector('.upsell-bundle__toggle-price');

      // State
      this.isEnabled = false;
      this.selectedProductId = null;
      this.selectedVariantId = null;
      this.upsellAdded = false; // Flag to track if upsell was already added
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.bindMasterToggle();
      this.bindProductSelection();
      this.bindSizeSelection();
      this.interceptFormSubmission();
    }

    bindMasterToggle() {
      if (!this.masterToggle) return;

      this.masterToggle.addEventListener('change', () => {
        this.isEnabled = this.masterToggle.checked;

        if (this.content) {
          this.content.style.display = this.isEnabled ? 'block' : 'none';
        }

        if (this.isEnabled) {
          // Auto-select first product when enabled
          this.selectFirstProduct();
        } else {
          // Reset selections when disabled
          this.resetSelections();
        }

        // Reset upsell added flag
        this.upsellAdded = false;
      });
    }

    selectFirstProduct() {
      const firstRadio = this.productRadios[0];
      if (firstRadio) {
        firstRadio.checked = true;
        this.handleProductSelection(firstRadio);
      }
    }

    bindProductSelection() {
      this.productRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          this.handleProductSelection(radio);
        });
      });
    }

    handleProductSelection(radio) {
      const productId = radio.dataset.productId;
      const price = radio.dataset.price;

      this.selectedProductId = productId;
      this.selectedVariantId = null;
      this.upsellAdded = false; // Reset flag when product changes

      // Update price display
      if (this.priceDisplay && price) {
        const formattedPrice = this.formatMoney(parseInt(price, 10));
        this.priceDisplay.textContent = '+' + formattedPrice;
      }

      // Remove selected state from all cards
      this.productCards.forEach((card) => {
        card.classList.remove('is-selected');
      });

      // Add selected state to clicked card
      const selectedCard = this.querySelector(`[data-upsell-card][data-product-id="${productId}"]`);
      if (selectedCard) {
        selectedCard.classList.add('is-selected');
      }

      // Hide all variant selectors and reset their state
      this.variantSelectors.forEach((selector) => {
        selector.style.display = 'none';

        // Reset size selections
        const sizeRadios = selector.querySelectorAll('[data-upsell-size-option]');
        sizeRadios.forEach((sizeRadio) => {
          sizeRadio.checked = false;
        });

        // Clear selected size display
        const selectedDisplay = selector.querySelector('[data-selected-size-value]');
        if (selectedDisplay) {
          selectedDisplay.textContent = '';
        }

        // Clear variant ID (keep auto variant if exists)
        const hiddenInput = selector.querySelector('[data-upsell-variant-id]');
        if (hiddenInput) {
          const autoVariant = selector.querySelector('[data-upsell-auto-variant]');
          hiddenInput.value = autoVariant ? autoVariant.value : '';
        }

        // Clear errors
        const errorMsg = selector.querySelector('[data-upsell-size-error]');
        if (errorMsg) {
          errorMsg.style.display = 'none';
        }
        const fieldset = selector.querySelector('[data-upsell-fieldset]');
        if (fieldset) {
          fieldset.classList.remove('variant-error');
        }
      });

      // Show variant selector for selected product
      const selectedVariantSelector = this.querySelector(
        `[data-upsell-variant-selector][data-product-id="${productId}"]`
      );
      if (selectedVariantSelector) {
        selectedVariantSelector.style.display = 'block';

        // If product has auto variant (no size option), set variant ID
        const autoVariant = selectedVariantSelector.querySelector('[data-upsell-auto-variant]');
        if (autoVariant) {
          this.selectedVariantId = autoVariant.value;
        }
      }
    }

    bindSizeSelection() {
      this.variantSelectors.forEach((selector) => {
        const sizeOptions = selector.querySelectorAll('[data-upsell-size-option]');

        sizeOptions.forEach((radio) => {
          radio.addEventListener('change', () => {
            this.handleSizeSelection(selector, radio);
          });
        });
      });
    }

    handleSizeSelection(selector, radio) {
      const productId = selector.dataset.productId;
      const variantId = radio.dataset.variantId;
      const displayValue = radio.dataset.displayValue;

      // Reset upsell flag when size changes
      this.upsellAdded = false;

      // Update hidden input
      const hiddenInput = selector.querySelector('[data-upsell-variant-id]');
      if (hiddenInput) {
        hiddenInput.value = variantId;
      }

      // Update selected size display in legend
      const selectedDisplay = selector.querySelector('[data-selected-size-value]');
      if (selectedDisplay) {
        selectedDisplay.textContent = displayValue;
      }

      // Store selected variant
      if (this.selectedProductId === productId) {
        this.selectedVariantId = variantId;
      }

      // Hide error message
      const errorMsg = selector.querySelector('[data-upsell-size-error]');
      if (errorMsg) {
        errorMsg.style.display = 'none';
      }

      const fieldset = selector.querySelector('[data-upsell-fieldset]');
      if (fieldset) {
        fieldset.classList.remove('variant-error');
      }
    }

    resetSelections() {
      this.selectedProductId = null;
      this.selectedVariantId = null;

      // Uncheck all product radios
      this.productRadios.forEach((radio) => {
        radio.checked = false;
      });

      // Remove selected state from all cards
      this.productCards.forEach((card) => {
        card.classList.remove('is-selected');
      });

      // Hide all variant selectors and reset
      this.variantSelectors.forEach((selector) => {
        selector.style.display = 'none';

        const sizeRadios = selector.querySelectorAll('[data-upsell-size-option]');
        sizeRadios.forEach((sizeRadio) => {
          sizeRadio.checked = false;
        });

        // Clear selected size display
        const selectedDisplay = selector.querySelector('[data-selected-size-value]');
        if (selectedDisplay) {
          selectedDisplay.textContent = '';
        }

        const hiddenInput = selector.querySelector('[data-upsell-variant-id]');
        if (hiddenInput) {
          const autoVariant = selector.querySelector('[data-upsell-auto-variant]');
          hiddenInput.value = autoVariant ? autoVariant.value : '';
        }

        const errorMsg = selector.querySelector('[data-upsell-size-error]');
        if (errorMsg) {
          errorMsg.style.display = 'none';
        }

        const fieldset = selector.querySelector('[data-upsell-fieldset]');
        if (fieldset) {
          fieldset.classList.remove('variant-error');
        }
      });
    }

    interceptFormSubmission() {
      // Find the main product form
      this.mainForm = document.getElementById(this.mainProductFormId);

      // Handle main form add-to-cart button clicks
      document.addEventListener(
        'click',
        async (event) => {
          const button = event.target.closest('[data-add-to-cart], [type="submit"][name="add"]');
          if (!button) return;

          const form = button.form || button.closest('form');
          if (!form) return;

          // Check if this is the main product form
          // Use getAttribute to avoid form inputs with name="id" shadowing the id property
          const formId = form.getAttribute('id');

          if (formId !== this.mainProductFormId) return;

          // If upsell not enabled, allow normal submission
          if (!this.isEnabled) return;

          // If no product selected, allow normal submission
          if (!this.selectedProductId) return;

          // Validate size selection
          if (!this.validateSelection()) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return false;
          }

          // If we already added the upsell, let the form submit normally
          if (this.upsellAdded) {
            this.upsellAdded = false; // Reset flag for next time
            return; // Allow normal form submission
          }

          // Prevent default form submission
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          // Immediately show loading state on button
          const originalDisabled = button.disabled;
          button.disabled = true;
          button.classList.add('is-loading');
          button.setAttribute('aria-busy', 'true');

          try {
            // Add upsell product to cart first
            await this.addUpsellToCart();

            // Longer delay to let cart state sync and avoid rate limiting
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Set flag so we skip upsell logic on re-click
            this.upsellAdded = true;

            // Restore button state before re-clicking
            button.disabled = originalDisabled;
            button.classList.remove('is-loading');
            button.setAttribute('aria-busy', 'false');

            // Create and dispatch a new click event that mimics user interaction
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
            });
            button.dispatchEvent(clickEvent);
          } catch (error) {
            // Restore button state on error
            button.disabled = originalDisabled;
            button.classList.remove('is-loading');
            button.setAttribute('aria-busy', 'false');
            console.error('Error in upsell flow:', error);
          }
        },
        true
      );

      // Handle sticky cart-bar add-to-cart button clicks
      document.addEventListener(
        'click',
        async (event) => {
          const cartBarButton = event.target.closest('[data-cart-bar-add-to-cart]');
          if (!cartBarButton) return;

          // If upsell not enabled, allow normal cart-bar flow
          if (!this.isEnabled) return;

          // If no product selected, allow normal cart-bar flow
          if (!this.selectedProductId) return;

          // Validate size selection
          if (!this.validateSelection()) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return false;
          }

          // If we already added the upsell, let the cart-bar flow continue normally
          // Don't reset flag here - it will be reset when main form button click passes through
          if (this.upsellAdded) {
            return; // Allow normal cart-bar submission
          }

          // Prevent cart-bar's default handling
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          // Show loading state on cart-bar button
          const originalDisabled = cartBarButton.disabled;
          cartBarButton.disabled = true;
          cartBarButton.classList.add('is-loading');
          cartBarButton.setAttribute('aria-busy', 'true');

          try {
            // Add upsell product to cart first
            await this.addUpsellToCart();

            // Longer delay to let cart state sync and avoid rate limiting
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Set flag so we skip upsell logic on re-click
            this.upsellAdded = true;

            // Restore button state before re-clicking
            cartBarButton.disabled = originalDisabled;
            cartBarButton.classList.remove('is-loading');
            cartBarButton.setAttribute('aria-busy', 'false');

            // Re-dispatch click on cart-bar button to trigger normal cart-bar flow
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
            });
            cartBarButton.dispatchEvent(clickEvent);
          } catch (error) {
            // Restore button state on error
            cartBarButton.disabled = originalDisabled;
            cartBarButton.classList.remove('is-loading');
            cartBarButton.setAttribute('aria-busy', 'false');
            console.error('Error in upsell flow (cart-bar):', error);
          }
        },
        true
      );
    }

    validateSelection() {
      const selectedVariantSelector = this.querySelector(
        `[data-upsell-variant-selector][data-product-id="${this.selectedProductId}"]`
      );

      if (!selectedVariantSelector) return true;

      // Check if product has auto variant (no size options)
      const autoVariant = selectedVariantSelector.querySelector('[data-upsell-auto-variant]');

      // If auto variant exists (no size options), validation passes
      if (autoVariant) {
        this.selectedVariantId = autoVariant.value;
        return true;
      }

      // Check if a size is selected
      const hiddenInput = selectedVariantSelector.querySelector('[data-upsell-variant-id]');

      if (!hiddenInput || !hiddenInput.value) {
        // Show error
        const errorMsg = selectedVariantSelector.querySelector('[data-upsell-size-error]');
        const fieldset = selectedVariantSelector.querySelector('[data-upsell-fieldset]');

        if (errorMsg) {
          errorMsg.style.display = 'block';
        }

        if (fieldset) {
          fieldset.classList.add('variant-error');
        }

        // Hide header during validation scroll (mobile)
        const header = document.querySelector('[data-header-sticky]');
        if (header) {
          header.classList.add('header-scroll-hide');
          header.dataset.lockedByValidation = 'true';
        }

        // Scroll to error
        selectedVariantSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Keep header hidden during smooth scroll
        setTimeout(() => {
          if (header && header.dataset.lockedByValidation === 'true') {
            header.classList.add('header-scroll-hide');
          }
        }, 300);

        return false;
      }

      this.selectedVariantId = hiddenInput.value;
      return true;
    }

    async addUpsellToCart(retryCount = 0) {
      if (!this.selectedVariantId) {
        console.warn('No variant ID selected for upsell');
        return false;
      }

      const variantId = parseInt(this.selectedVariantId, 10);
      if (isNaN(variantId)) {
        console.error('Invalid variant ID:', this.selectedVariantId);
        return false;
      }

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
              },
            ],
          }),
        });

        // Handle rate limiting (429)
        if (response.status === 429) {
          if (retryCount < 2) {
            // Wait and retry
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return this.addUpsellToCart(retryCount + 1);
          }
          console.error('Rate limited by Shopify, please try again');
          return false;
        }

        // Check content type before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.error('Unexpected response type:', contentType);
          return false;
        }

        const data = await response.json();

        if (!response.ok) {
          console.error(
            'Failed to add upsell item:',
            data.message || data.description || 'Unknown error'
          );
          return false;
        }

        return true;
      } catch (error) {
        console.error('Error adding upsell item:', error);
        return false;
      }
    }

    formatMoney(cents) {
      let formatted;
      if (typeof window.theme !== 'undefined' && typeof window.theme.formatMoney === 'function') {
        formatted = window.theme.formatMoney(cents, window.theme.moneyFormat);
      } else {
        // Fallback formatting
        const amount = (cents / 100).toFixed(2);
        formatted = '$' + amount;
      }

      // Strip HTML tags that money formatters may include
      const temp = document.createElement('div');
      temp.innerHTML = formatted;
      return temp.textContent || temp.innerText || formatted;
    }
  }

  customElements.define('product-upsell-bundle', ProductUpsellBundle);
}
